/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaImage } from 'react-icons/fa';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { update } from '../../services/item';
import { uploadPic, uploadPicFromUrl } from '../../services/pic';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { ModalGlobal } from '../modal/modal.Global';
import axios from 'axios';
import { saveAll } from '../../store/items';
import { ModalConfirm } from '../modal/modal.confirm';
import { setOpenModales } from '../../store/global';
import { renderIconBySlug } from '../../content/icons';
import { ColorsSwatch } from '../form/colorsSwatch';
import { IconPicker } from '../icons/icon.Picker';
import {
  WidgetSettingsRoot,
  WidgetSettingsSection,
  SettingsInput,
} from '../form/settings.Widget';

export const PicSettings = props => {
  const { picId, itemId, pic } = props;

  const item = useSelector(state => state.items[itemId]);
  const isPicModalOpen = useSelector(
    state => state.global.openModales?.[picId] === true,
  );
  const linkIds = useMemo(() => {
    if (!Array.isArray(item?.links)) {
      return [];
    }
    return Array.from(new Set(item.links.map(linkObj => getId(linkObj)).filter(Boolean)));
  }, [item?.links]);
  const linkIdsKey = useMemo(() => linkIds.join(','), [linkIds]);
  const linkedItems = useSelector(state => {
    const byId = {};
    linkIds.forEach(id => {
      if (state.items[id]) {
        byId[id] = state.items[id];
      }
    });
    return byId;
  }, shallowEqual);

  const [picSize, setPicSize] = useState({ width: 100, height: 100 });
  const missingItemIdsRef = useRef(new Set());
  const pendingItemIdsRef = useRef(new Set());
  const dispatch = useDispatch();

  const getItemsBatch = async ids => {
    const uniqueIds = Array.from(
      new Set(
        (ids || []).filter(
          id =>
            id &&
            !pendingItemIdsRef.current.has(id) &&
            !missingItemIdsRef.current.has(id),
        ),
      ),
    );
    if (uniqueIds.length === 0) {
      return;
    }
    uniqueIds.forEach(id => pendingItemIdsRef.current.add(id));
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/items/batch`, {
        ids: uniqueIds,
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const foundIds = new Set(rows.filter(entry => entry?._id).map(entry => entry._id));
      rows.forEach(entry => {
        if (entry?._id) {
          dispatch(saveAll({ item: entry }));
        }
      });
      uniqueIds.forEach(id => {
        if (!foundIds.has(id)) {
          missingItemIdsRef.current.add(id);
        }
      });
    } catch (error) {
      uniqueIds.forEach(id => missingItemIdsRef.current.add(id));
    } finally {
      uniqueIds.forEach(id => pendingItemIdsRef.current.delete(id));
    }
  };

  useEffect(() => {
    if (!isPicModalOpen || !item || linkIds.length === 0) {
      return;
    }
    const missingIds = linkIds.filter(id => !linkedItems[id]);
    if (missingIds.length > 0) {
      getItemsBatch(missingIds);
    }
  }, [isPicModalOpen, item?._id, linkIdsKey, linkedItems]);

  const picItems = useMemo(
    () => Object.values(linkedItems).filter(linkedItem => linkedItem?.type === 'pic'),
    [linkedItems],
  );

  const persistPicPatch = useCallback(
    patch => {
      if (!item?.pics) {
        return;
      }
      const clonedPics = deepCloneArray(item.pics);
      const picIdx = clonedPics.findIndex(picObj => getId(picObj) === picId);
      if (picIdx === -1) {
        return;
      }
      clonedPics[picIdx][picId] = {
        ...clonedPics[picIdx][picId],
        ...patch,
      };
      update(`items/${itemId}`, 'pics', clonedPics);
    },
    [item?.pics, itemId, picId]
  );

  const handleNewPic = (type, e) => {
    if (type === 'local') {
      uploadPic(e).then(res => persistPicPatch({ url: res.data.url }));
    }
    if (type === 'url' && pic?.url !== e) {
      uploadPicFromUrl(e).then(res => persistPicPatch({ url: res.data.url }));
    }
  };

  const deletePic = () => {
    const clonedPics = deepCloneArray(item.pics);
    const picIdx = clonedPics.findIndex(picObj => getId(picObj) === picId);
    if (picIdx > -1) {
      clonedPics.splice(picIdx, 1);
      update(`items/${itemId}`, 'pics', clonedPics);
    }
  };

  const getMeta = (url, cb) => {
    const img = new Image();
    img.onload = () => cb(null, img);
    img.onerror = err => cb(err);
    img.src = url;
  };

  useEffect(() => {
    if (pic?.url) {
      getMeta(pic.url, (err, img) => {
        setPicSize({ width: img.naturalWidth, height: img.naturalHeight });
      });
    } else {
      setPicSize({ width: 100, height: 100 });
    }
  }, [pic]);

  const iconPreview = pic?.iconSlug
    ? renderIconBySlug(pic.iconSlug, {
        size: 96,
        color: 'var(--contrast)',
      })
    : null;

  const handleSelectIcon = useCallback(
    slug => {
      persistPicPatch({ iconSlug: slug });
    },
    [persistPicPatch]
  );

  const handleClearIcon = useCallback(() => {
    persistPicPatch({ iconSlug: null });
  }, [persistPicPatch]);

  const handleBgColorChange = color => {
    if (color === 'transparent') {
      persistPicPatch({ bgColor: null });
    } else {
      persistPicPatch({ bgColor: color });
    }
  };

  const handleClearImage = () => {
    persistPicPatch({ url: null });
  };

  const avatarBgColors = [
    'transparent',
    'primary',
    'primary-l',
    'primary-d',
    'secondary',
    'secondary-l',
    'secondary-d',
    'contrast',
    'contrast-l',
    'main',
    'main-d',
    'red',
    'orange',
    'yellow',
    'lime',
    'green',
    'blue',
    'purple',
    'pink',
  ];
  const hasImage = Boolean(pic?.url);
  const dimensionsLabel =
    hasImage && Number.isFinite(picSize.width) && Number.isFinite(picSize.height)
      ? `${picSize.width} × ${picSize.height}px`
      : null;

  return (
    <ModalGlobal
      title='edition'
      idToOpen={picId}
      width={960}
      maxWidthClass='max-w-6xl'
    >
      <WidgetSettingsRoot>
        <WidgetSettingsSection title='Image'>
          <div className='flex flex-col lg:flex-row gap-1'>
            <div className='flex-2 min-w-0'>
              <div
                className='rounded border border-primary border-opacity-30 bg-main bg-opacity-40 overflow-hidden flex items-center justify-center relative'
                style={{ minHeight: 360, maxHeight: 'calc(100svh - 230px)' }}
              >
                {pic?.iconSlug ? (
                  <div className='flex flex-col items-center justify-center gap-1 py-4 px-2 text-center min-h-360'>
                    <div className='text-4xl text-contrast text-opacity-90'>
                      {iconPreview}
                    </div>
                    <span className='text-2xs text-secondary'>{pic.iconSlug}</span>
                    {hasImage && (
                      <button
                        type='button'
                        className='text-xs text-primary hover:text-secondary'
                        onClick={handleClearIcon}
                      >
                        Afficher l'image à la place de l'icône
                      </button>
                    )}
                  </div>
                ) : hasImage ? (
                  <img
                    src={pic.url}
                    alt='Prévisualisation'
                    className='block max-w-full object-contain'
                    style={{ maxHeight: 'calc(100svh - 230px)' }}
                  />
                ) : (
                  <div className='min-h-360 w-full flex flex-col items-center justify-center gap-05 text-contrast text-opacity-50'>
                    <FaImage className='text-4xl' />
                    <span className='text-sm'>Aucune image sélectionnée</span>
                  </div>
                )}
              </div>
              <div className='mt-05 flex flex-wrap items-center justify-between gap-05 text-xs text-contrast text-opacity-60'>
                <span>{dimensionsLabel || 'Aucune dimension détectée'}</span>
                {hasImage && (
                  <a
                    href={pic.url}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-03 text-primary hover:text-secondary'
                  >
                    <FaExternalLinkAlt />
                    Ouvrir l’image originale
                  </a>
                )}
              </div>
            </div>

            <div className='flex-1 min-w-240 flex flex-col gap-1'>
              <div className='w-full'>
                <label className='relative block py-03 cursor-pointer w-full bg-primary rounded shadow flex items-center justify-center gap-1'>
                  <FaImage /> Depuis l'ordinateur
                  <input
                    className='invisible absolute'
                    type='file'
                    name='file'
                    onChange={e => handleNewPic('local', e)}
                    accept='image/*'
                  />
                </label>
              </div>

              <div className='flex w-full'>
                <SettingsInput
                  className=''
                  initValue={pic?.url}
                  placeholder={'depuis une url'}
                  onBlur={value => handleNewPic('url', value)}
                />
              </div>

              <div className='flex gap-1 flex-wrap'>
                {picItems.map((picItem, i) => {
                  const linkedPicObj = picItem?.pics?.[0];
                  const linkedPicId = linkedPicObj
                    ? Object.keys(linkedPicObj)?.[0]
                    : null;
                  const linkedPicUrl = linkedPicId
                    ? linkedPicObj?.[linkedPicId]?.url
                    : null;
                  if (!linkedPicUrl) {
                    return null;
                  }
                  return (
                    <div
                      key={i}
                      onClick={() => handleNewPic('url', linkedPicUrl)}
                    >
                      <div
                        className='rounded shadow cursor-pointer w-3 h-3 border border-primary border-opacity-30'
                        style={{
                          background: `url(${linkedPicUrl}) no-repeat center / cover`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div>
                <strong className='text-sm text-contrast text-opacity-80'>
                  Couleur de fond
                </strong>
                <ColorsSwatch
                  colors={avatarBgColors}
                  currentColor={pic?.bgColor ?? 'transparent'}
                  onClick={handleBgColorChange}
                />
              </div>

              {hasImage && (
                <button
                  type='button'
                  className='text-xs text-secondary hover:text-primary underline text-left'
                  onClick={handleClearImage}
                >
                  Supprimer l'image actuelle
                </button>
              )}
            </div>
          </div>
        </WidgetSettingsSection>

        <WidgetSettingsSection title='Icône'>
          <IconPicker
            size={28}
            value={pic?.iconSlug}
            onChange={(label, slug) => handleSelectIcon(slug ?? null)}
            placeholder='Rechercher une icône'
            label='Icône'
            previewSize={32}
            inputClassName='mt-1'
            gridClassName='mx-1'
            maxHeight={150}
            onClear={handleClearIcon}
          />
        </WidgetSettingsSection>
      </WidgetSettingsRoot>

      <em
        onDoubleClick={e => {
          e.stopPropagation();
          dispatch(
            setOpenModales({ id: 'delete' + picId + itemId, open: true })
          );
        }}
        className='text-secondary opacity-50 block text-right'
      >
        Double cliquer pour supprimer
      </em>
      <ModalConfirm
        title='Suppression'
        idToOpen={'delete' + picId + itemId}
        confirmCallback={() => {
          deletePic();
          dispatch(setOpenModales({ id: picId, open: false }));
        }}
        confirmText='Supprimer'
      >
        Voulez-vous vraiment supprimer cette image ?
      </ModalConfirm>
    </ModalGlobal>
  );
};
